import {
    ReactNode, useCallback, useMemo,
} from 'react';
import { PicklistValue } from '@rainbow-modules/listview/lib/components/AuditLogs/types';
import { useReduxForm } from '@rainbow-modules/hooks';
import { Option, RenderIf } from 'react-rainbow-components';
import Picklist from 'components/Picklist';

interface LanguagePickerProps {
    id?: string;
    className?: string;
    name?: string;
    label?: string;
    tabIndex?: number | string;
    header?: string;
    hideHeader?: boolean;
    placeholder?: string;
    error?: ReactNode;
    required?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    value?: string;
    onChange?: (value: string) => void;
    allowDeselect?: boolean;
    languages?: string[];
}

const options = [
    { name: 'en', label: 'English', value: 'en' },
    { name: 'es', label: 'Spanish', value: 'es' },
    { name: 'multi', label: 'Multi-Language', value: 'auto' },
];

const LanguagePicker = (props: LanguagePickerProps) => {
    const {
        value: valueInProps,
        onChange = () => {},
        id,
        className,
        name,
        label,
        tabIndex,
        header = 'Preferred Language',
        hideHeader = false,
        placeholder,
        error,
        required,
        disabled,
        readOnly,
        languages = ['en', 'es'],
        allowDeselect,
    } = useReduxForm(props);

    const pickListvalue = useMemo(() => {
        if (!valueInProps) return undefined;
        return options.find((option) => option.value === valueInProps);
    }, [valueInProps]);

    const handleOnChange = useCallback(
        (value: PicklistValue) => onChange(value.value),
        [onChange],
    );

    const pickListOptions = useMemo(
        () => options
            .filter((option) => languages.includes(option.name))
            .map((option) => <Option key={`language-picker-option__${option.name}`} {...option} />),
        [languages],
    );

    return (
        <Picklist
            id={id}
            name={name}
            className={className}
            label={label}
            labelAlignment="left"
            tabIndex={tabIndex}
            required={required}
            placeholder={placeholder}
            value={pickListvalue}
            disabled={disabled}
            readOnly={readOnly}
            onChange={handleOnChange}
            error={error}
            borderRadius="semi-rounded"
        >
            <RenderIf isTrue={!hideHeader}>
                <Option label={header} variant="header" />
            </RenderIf>
            {pickListOptions}
            <RenderIf isTrue={allowDeselect}>
                <Option label="No Language" value={null} />
            </RenderIf>
        </Picklist>
    );
};

export default LanguagePicker;
